import {
  IconAlertTriangle,
  IconBarcode,
  IconBowlSpoon,
  IconCalculator,
  IconCaretUpFilled,
  IconInfoCircle,
  IconShoppingCart,
  IconZoom,
} from "@tabler/icons-react";
import { useStore } from "@tanstack/react-store";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { getMacros } from "../stores/all-products";
import {
  addCustomization,
  customizationsStore,
  removeCustomization,
  withCustomizations,
} from "../stores/customizations-store";
import { addMeal } from "../stores/meals";
import {
  addShoppingProduct,
  getShoppingProductQuantity,
  isShoppingProduct,
  shoppingProductsIndexStore,
} from "../stores/shopping-products";
import {
  getGreenscore,
  getNovaScore,
  getNutriscore,
  getProductName,
  type ProductV3,
} from "../types";
import {
  isHighCalories,
  isHighFat,
  isHighFiber,
  isHighProtein,
  isHighSalt,
  isHighSugar,
  isMediumCalories,
  isMediumFat,
  isMediumFiber,
  isMediumProtein,
  isMediumSalt,
  isMediumSugar,
} from "../utils/health";
import { useLocalStorage } from "../utils/local-storage";
import { useProductMealCountToday } from "../utils/meals";
import { EditableText } from "./EditableText";
import { Modal } from "./modal";
import { Quantity } from "./quantity";

type FoodProps = {
  product: ProductV3;
};

const decimal = (value: number, decimals: number) =>
  Number(value.toFixed(decimals).replace(".0", ""));

const FoodCard = ({ product }: FoodProps) => {
  // Score image logic
  // Import score helpers
  // (Assume getNutriscore, getNovaScore, getGreenscore are imported from '../types')
  const nutriScore = getNutriscore(product);
  const greenScore = getGreenscore(product);
  const novaScore = getNovaScore(product);

  // Image path helpers
  const nutriScoreImg = nutriScore
    ? `/images/nutriscore-${nutriScore.toLowerCase()}.svg`
    : undefined;
  const novaScoreImg = novaScore ? `/images/nova-group-${novaScore}.svg` : undefined;
  const greenScoreImg = greenScore
    ? `/images/green-score-${greenScore.toLowerCase()}.svg`
    : undefined;

  // Rerenders when customizations change.
  useStore(customizationsStore);
  const router = useRouter();

  const [zoomImage, setZoomImage] = useState(-1);
  useStore(shoppingProductsIndexStore);
  const isShopping = isShoppingProduct(product.code!);
  const mealCount = useProductMealCountToday(product.code!);

  const addShopping = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isShopping) {
      addShoppingProduct(product);
    } else {
      router.push("/buy/", { scroll: false });
    }
  };

  const myProduct = withCustomizations(product);
  const servingSize = Number(myProduct.serving_quantity) || 100;
  const nutrients: Record<keyof ProductV3["nutriments"] | string, any> = myProduct.nutriments || {};
  const macros = getMacros(myProduct);

  const pictures = [
    { alt: "front", thumbnail: myProduct.image_thumb_url, full: myProduct.image_url },
    {
      alt: "ingredients",
      thumbnail: myProduct.image_ingredients_thumb_url,
      full: myProduct.image_ingredients_url,
    },
    {
      alt: "nutrition",
      thumbnail: myProduct.image_nutrition_thumb_url,
      full: myProduct.image_nutrition_url,
    },
  ].filter((pic) => !!pic.thumbnail);

  useEffect(() => {
    if (zoomImage >= 0) {
      pictures.forEach((pic, index) => {
        if (pic.full && index !== zoomImage) {
          new Image().src = pic.full;
        }
      });
    }
  }, [zoomImage, pictures]);

  const originalProductName = getProductName(product);
  const productName = getProductName(myProduct) || originalProductName;
  const productKcal: number = macros.energy;
  const hasNutrients =
    Object.keys(nutrients).filter(
      (key) =>
        key.endsWith("_100g") &&
        ![
          "energy_100g",
          "energy-kcal_100g",
          "fat_100g",
          "carbohydrates_100g",
          "proteins_100g",
        ].includes(key)
    ).length > 0;

  const [showServing, setShowServing] = useLocalStorage("showServing", false);

  const handleServingSizeChange = (newValue: string) => {
    const size = Math.round(Number(newValue.trim()));
    if (size === 0 || isNaN(size)) {
      removeCustomization(myProduct.code!, { serving_quantity: undefined });
    } else {
      addCustomization(myProduct.code!, { serving_quantity: size as any as string });
    }
  };

  // Calculate per serving values
  const getDisplayValue = (value: number) => {
    return showServing ? (value * servingSize) / 100 : value;
  };

  return (
    <div className="w-full min-h-full mx-auto">
      <Modal
        visible={zoomImage >= 0}
        onClose={() => setZoomImage(-1)}
        onPrevious={
          zoomImage >= 1
            ? () => setZoomImage((idx) => (idx - 1 + pictures.length) % pictures.length)
            : undefined
        }
        onNext={
          zoomImage > -1 && zoomImage < pictures.length - 1
            ? () => setZoomImage((idx) => (idx + 1) % pictures.length)
            : undefined
        }
        totalItems={pictures.length}
        currentItemIndex={zoomImage}
      >
        <img
          src={pictures[zoomImage]?.full}
          alt={pictures[zoomImage]?.alt}
          className="max-w-full max-h-auto select-none"
        />
      </Modal>

      <div className="px-4 pb-6">
        <div className="flex items-center justify-start gap-4 mb-4">
          {/* Score Images Section */}
          {(nutriScoreImg || novaScoreImg || greenScoreImg) && (
            <>
              {nutriScoreImg && nutriScore && (
                <a
                  href={`https://en.wikipedia.org/wiki/Nutri-Score`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Learn more about Nutri-Score ${nutriScore.toUpperCase()}`}
                >
                  <img
                    src={nutriScoreImg}
                    alt={`Nutri-Score ${nutriScore}`}
                    className="h-8 w-auto"
                    title={`Nutri-Score: ${nutriScore.toUpperCase()}`}
                    loading="lazy"
                  />
                </a>
              )}
              {greenScoreImg && greenScore && (
                <a
                  href={`https://en.wikipedia.org/wiki/Eco-score`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Learn more about Green-Score ${greenScore}`}
                >
                  <img
                    src={greenScoreImg}
                    alt={`Green-Score ${greenScore}`}
                    className="h-8 w-auto"
                    title={`Green Score: ${greenScore}`}
                    loading="lazy"
                  />
                </a>
              )}
              {novaScoreImg && novaScore && (
                <a
                  href={`https://en.wikipedia.org/wiki/Nova_classification`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Learn more about Nova Group ${novaScore}`}
                >
                  <img
                    src={novaScoreImg}
                    alt={`Nova Group ${novaScore}`}
                    className="h-8 w-auto"
                    title={`Nova Group: ${novaScore}`}
                    loading="lazy"
                  />
                </a>
              )}
            </>
          )}

          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-0">
            <IconBarcode strokeWidth={1.5} className="size-6 inline-block" />
            <EditableText
              value={myProduct.code || ""}
              onChange={(value) => {
                if (value) {
                  addCustomization(myProduct.code!, { code: value });
                }
              }}
            />
          </span>
        </div>

        <section className="pb-4">
          <div className="flex space-x-2 flex-wrap">
            {pictures.map(
              (pic, index) =>
                pic.thumbnail &&
                pic.full !== undefined && (
                  <div
                    key={pic.thumbnail + index}
                    className="size-16 rounded-2xl relative overflow-hidden flex items-center justify-center"
                    onClick={() => setZoomImage(index)}
                  >
                    <div
                      className="absolute size-full inset-0 z-[-1] blur-md"
                      style={{
                        background: `url(${pic.thumbnail}) center center / contain`,
                      }}
                    />
                    <img
                      src={pic.thumbnail}
                      alt={pic.alt}
                      className="h-16 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).parentElement!.style.display = "none";
                      }}
                    />
                  </div>
                )
            )}
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(
                `${productName}, ${originalProductName}, ${myProduct.brands || ""}`
              )}&udm=2`}
              target="_blank"
              rel="noopener noreferrer"
              className="relative size-16 rounded-2xl cursor-pointer flex items-center justify-center bg-gray-300 dark:bg-gray-700"
            >
              <IconZoom className="h-8 w-8 text-gray-600 dark:text-gray-300" />
            </a>
          </div>
        </section>

        <div className="flex gap-2 mb-4">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addMeal({
                products: [
                  {
                    productId: myProduct.code!,
                    servingSize: Number(myProduct.serving_quantity) || 100,
                  },
                ],
                eatenAt: new Date().toISOString(),
                createdAt: new Date().toISOString(),
              });
            }}
            className="flex-1 bg-sky-500 dark:bg-sky-600 text-white font-semibold py-3 px-4 rounded-4xl flex items-center justify-center gap-2"
          >
            {mealCount && mealCount > 0 ? (
              <span className="rounded-md px-2 pb-0.5 bg-white text-black inline-block pt-0.5 text-sm font-semibold">
                {mealCount}
              </span>
            ) : (
              <IconBowlSpoon className="size-6" />
            )}
            <span>Track Meal</span>
          </button>
          <button
            onClick={addShopping}
            className="flex-1 bg-lime-500 dark:bg-lime-600 text-white font-semibold py-3 px-4 rounded-4xl flex items-center justify-center gap-2"
          >
            {isShopping ? (
              <span className="rounded-md px-2 pb-0.5 bg-white text-black inline-block pt-0.5 text-sm font-semibold">
                {getShoppingProductQuantity(myProduct.code!)}
              </span>
            ) : (
              <IconShoppingCart className="size-6" />
            )}
            <span>Groceries</span>
          </button>
        </div>

        <div className="text-gray-500 dark:text-gray-400 pb-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">Macronutrients</h3>
            <div className={`flex items-center ${showServing ? "text-blue-500" : ""}`}>
              <label className="flex items-center cursor-pointer ">
                <input
                  type="checkbox"
                  checked={showServing}
                  onChange={(e) => setShowServing(e.target.checked)}
                  className="hidden" /* Hide the checkbox but keep it in the DOM */
                />
                <IconCalculator
                  strokeWidth={1.5}
                  className={`size-5 ${showServing ? "text-blue-500" : "text-gray-500"}`}
                />
                <span className="text-sm mx-1">per</span>
              </label>

              {showServing ? (
                <EditableText
                  disabled={!showServing}
                  numeric
                  value={servingSize.toString()}
                  onChange={handleServingSizeChange}
                  className="text-sm bg-sky-50 dark:bg-sky-950 p-1 rounded-md"
                />
              ) : (
                <span
                  className="text-sm py-1 rounded-md"
                  onClick={() => setShowServing(!showServing)}
                >
                  100
                </span>
              )}
              <span className="text-sm ml-1">g</span>
            </div>
          </div>

          <table className="table-auto w-full border-collapse border border-slate-400 dark:border-slate-500 bg-white dark:bg-gray-800">
            <tbody>
              {Object.entries(macros).map(([key, value]) => (
                <tr key={key} className="border border-slate-300 dark:border-slate-700">
                  <td className="border border-slate-300 dark:border-slate-700 p-2 text-nowrap text-sm capitalize w-3/5 shrink">
                    {key}
                  </td>
                  <td className="border border-slate-300 dark:border-slate-700 p-2 text-right text-sm">
                    {showServing && (
                      <IconCalculator
                        strokeWidth={1.5}
                        className="float-left mt-0.5 size-4 text-gray-400 dark:text-gray-400"
                      />
                    )}
                    {key === "energy" && isHighCalories(productKcal, 100) && (
                      <IconAlertTriangle
                        className="inline size-4 -mt-0.5 mr-1 text-red-500"
                        title="Very high calorie content"
                      />
                    )}
                    {key === "energy" && isMediumCalories(productKcal, 100) && (
                      <IconAlertTriangle
                        className="inline size-4 -mt-0.5 mr-1 text-amber-500"
                        title="High calorie content"
                      />
                    )}
                    {key === "fat" &&
                      (isHighFat(value, 100) ? (
                        <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-red-500" />
                      ) : isMediumFat(value, 100) ? (
                        <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-amber-500" />
                      ) : null)}
                    {key === "proteins" &&
                      (isHighProtein(value, 100) ? (
                        <IconCaretUpFilled className="inline size-4  mr-1 text-green-500" />
                      ) : isMediumProtein(value, 100, productKcal) ? (
                        <IconCaretUpFilled className="inline size-4  mr-1 text-sky-500" />
                      ) : null)}

                    <EditableText
                      disabled={showServing}
                      numeric
                      value={decimal(value, 1).toString()}
                      onChange={(newValue) => {
                        const nutrimentKey = {
                          energy: "energy-kcal_100g",
                          fat: "fat_100g",
                          proteins: "proteins_100g",
                          carbs: "carbohydrates_100g",
                        }[key] as keyof ProductV3["nutriments"];
                        const customization = {
                          nutriments: { [nutrimentKey]: newValue ? Number(newValue) : undefined },
                        };
                        if (!newValue) {
                          removeCustomization(product.code!, customization);
                        } else {
                          addCustomization(product.code!, customization);
                        }
                      }}
                    >
                      <Quantity
                        value={getDisplayValue(value)}
                        unit={key === "energy" ? "kcal" : "g"}
                      />
                    </EditableText>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasNutrients && (
          <div className="text-gray-500 dark:text-gray-400 pb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Micronutrients</h3>
            <table className="table-auto w-full border-collapse border border-slate-400 dark:border-slate-500 bg-white dark:bg-gray-800">
              <tbody>
                {Object.entries(nutrients)
                  .filter(([key, value]) => {
                    if (
                      !key.includes("_100g") ||
                      key.includes("_prepared") ||
                      key.includes("energy") ||
                      key.includes("carbohydrates") ||
                      key.includes("fat") ||
                      key.includes("proteins")
                    ) {
                      return false;
                    }
                    if (product.id && nutrients[key.replace("_100g", "") + "_unit"] !== "g") {
                      return false;
                    }
                    return value > Number.EPSILON;
                  })
                  .sort(([, a], [, b]) => Number(b) - Number(a))
                  .map(([key, value]) => {
                    key = key.replace("_100g", "");
                    const wikiSearchUrl = `https://wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(
                      key.replaceAll("-", " ")
                    )}`;
                    return (
                      <tr key={key} className="border border-slate-300 dark:border-slate-700">
                        <td className="border border-slate-300 dark:border-slate-700 p-2 text-sm capitalize w-3/5 shrink">
                          {key.replaceAll("-", " ")}
                          <a
                            href={wikiSearchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-gray-500 dark:decoration-gray-400"
                          >
                            <IconInfoCircle className="inline size-4 -mt-0.5 ml-1 text-gray-400 dark:text-gray-400" />
                          </a>
                        </td>
                        <td className="border border-slate-300 dark:border-slate-700 p-2 text-right text-sm">
                          {showServing && (
                            <IconCalculator
                              strokeWidth={1.5}
                              className="float-left mt-0.5 size-4 text-gray-400 dark:text-gray-400"
                            />
                          )}
                          {["fiber", "fibers"].includes(key) &&
                            (isHighFiber(value, 100) ? (
                              <IconCaretUpFilled className="inline size-4  mr-1 text-green-500" />
                            ) : (
                              isMediumFiber(value, 100, productKcal) && (
                                <IconCaretUpFilled className="inline size-4  mr-1 text-sky-500" />
                              )
                            ))}
                          {["sugar", "sugars"].includes(key) &&
                            (isHighSugar(value, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-red-500" />
                            ) : isMediumSugar(value, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-amber-500" />
                            ) : null)}
                          {["salt", "salts"].includes(key) &&
                            (isHighSalt(value, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-red-500" />
                            ) : isMediumSalt(value, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-amber-500" />
                            ) : null)}
                          {["sodium"].includes(key) &&
                            (isHighSalt(value * 2.5, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-red-500" />
                            ) : isMediumSalt(value * 2.5, 100) ? (
                              <IconAlertTriangle className="inline size-4 -mt-0.5 mr-1 text-amber-500" />
                            ) : null)}
                          <Quantity value={getDisplayValue(value)} />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
        {product.ingredients && (
          <div className="py-4 text-xs text-center">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Ingredients</h3>
            {product.ingredients
              .filter((i) => i.id?.startsWith("en:"))
              .sort((a, b) => b.percent_estimate! - a.percent_estimate!)
              .map((i) => (
                <a
                  key={i.id!}
                  href={`https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(
                    i.id!.replaceAll("en:", "").replaceAll("-", " ")
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-gray-200 dark:bg-gray-700 rounded-full px-2 py-1 text-gray-700 dark:text-gray-300 mr-1 mb-1 capitalize"
                >
                  {`${i.id!.replaceAll("en:", "").replaceAll("-", " ")} ${decimal(
                    i.percent_estimate ?? 0,
                    1
                  )}%`}
                </a>
              ))}
          </div>
        )}
        {product.last_modified_t && (
          <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-4">
            Last update {new Date(product.last_modified_t * 1000).toISOString().split("T")[0]}
          </div>
        )}
      </div>
    </div>
  );
};

export { FoodCard };
